import type React from 'react';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { TypedTranslate } from '@change-corgi/core-react/i18n';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { SocialButton } from '@change-corgi/design-system-legacy/components/social';
import { iconCheck } from '@change-corgi/design-system-legacy/icons';
import { Flex } from '@change-corgi/design-system-legacy/layout';
import type { ResponsiveValue } from '@change-corgi/design-system-legacy/theme';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { ShareAction as ShareActionType, ShareChannel } from 'src/app/shared/hooks/share';

import { useShareButton } from './hooks/useShareButton';

type Channel = Exclude<ShareChannel, 'qrcode' | 'flyer'>;
type ShareButtonProps = {
	buttonConfig: {
		ShareAction: ShareActionType<Channel>['ShareAction'];
		channel: Channel;
		label: TranslationKey;
		successLabel?: TranslationKey;
		labelSize?: ResponsiveValue<'default' | 'small'>;
	};
	isSelected?: boolean;
	isSunriseEnabled?: boolean;
	onBeforeAction?: () => void | Promise<void>;
};
export const ShareButton: React.FC<ShareButtonProps> = ({
	buttonConfig: { ShareAction, channel, label, successLabel, labelSize },
	isSelected = false,
	isSunriseEnabled = false,
	onBeforeAction,
}) => {
	const { data, actions } = useShareButton({ label, successLabel });
	const socialButtonStyling = isSelected
		? { width: '100%', borderWidth: '2px', borderColor: 'black' }
		: { width: '100%' };
	return (
		<ShareAction onBeforeAction={onBeforeAction} onAfterAction={actions.afterShare}>
			<SocialButton channel={channel} sx={socialButtonStyling}>
				<Flex sx={{ justifyContent: 'center', alignItems: 'center' }}>
					<Text
						aria-live="polite"
						as="span"
						size={labelSize}
						sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
					>
						<TypedTranslate value={data.buttonLabel} />
					</Text>
					{data.clickedState ? (
						<Icon
							data-testid="share-button-success-icon"
							icon={iconCheck}
							size={20}
							ml={4}
							color={isSunriseEnabled ? 'sunrise.semantic.typography.success' : 'typography-positive'}
						/>
					) : null}
				</Flex>
			</SocialButton>
		</ShareAction>
	);
};
